import { Box, SimpleGrid } from '@chakra-ui/core';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { MdBlock, MdBlurOn, MdOutlineBlurCircular } from 'react-icons/md';
import { SectionLabel } from '../SectionLabel';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BlurEffect } from '../videoEffectTypes';
import { BlurCard } from './BlurCard';

const BLUR_EFFECTS: ReadonlyArray<{ id: BlurEffect; label: string; icon: IconType }> = [
  { id: 'none', label: 'none', icon: MdBlock },
  { id: 'blur-light', label: 'slightBlur', icon: MdOutlineBlurCircular },
  { id: 'blur-heavy', label: 'blur', icon: MdBlurOn },
];

interface BlurSectionProps {
  onBlurSelect: (effect: BlurEffect) => void;
}

export const BlurSection = memo(function BlurSection({ onBlurSelect }: BlurSectionProps) {
  const { t } = useTranslation('videoInterviewEffect');

  const isApplying = VideoEffectStore.useStoreState((state) => state.isApplyingEffect);
  const activeBlurEffect = VideoEffectStore.useStoreState((state) => state.activeBlurEffect);
  const [pendingId, setPendingId] = useState<BlurEffect | null>(null);

  const prevIsApplyingRef = useRef(isApplying);
  useEffect(() => {
    // istanbul ignore next
    if (prevIsApplyingRef.current && !isApplying) {
      setPendingId(null);
    }
    prevIsApplyingRef.current = isApplying;
  }, [isApplying]);

  const isApplyingRef = useRef(isApplying);
  useEffect(() => {
    isApplyingRef.current = isApplying;
  });

  const handleSelect = useCallback(
    (id: BlurEffect) => {
      if (isApplyingRef.current) return;
      setPendingId(id);
      onBlurSelect(id);
    },
    [onBlurSelect],
  );

  return (
    <Box flexShrink={0} mb={4}>
      <SectionLabel>{t('blurEffects.title')}</SectionLabel>
      <SimpleGrid spacing={2} columns={3}>
        {BLUR_EFFECTS.map((effect) => (
          <MemoizedBlurCard
            effectId={effect.id}
            label={t(`blurEffects.${effect.label}`)}
            icon={effect.icon}
            isActive={activeBlurEffect === effect.id && activeBlurEffect !== 'none'}
            isLoading={pendingId === effect.id}
            isApplying={isApplying}
            onSelect={handleSelect}
            key={effect.id}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
});

interface MemoizedBlurCardProps {
  effectId: BlurEffect;
  label: string;
  icon: IconType;
  isActive: boolean;
  isLoading: boolean;
  isApplying: boolean;
  onSelect: (id: BlurEffect) => void;
}

const MemoizedBlurCard = memo(function MemoizedBlurCard({
  effectId,
  label,
  icon,
  isActive,
  isLoading,
  isApplying,
  onSelect,
}: MemoizedBlurCardProps) {
  const handleClick = useCallback(() => onSelect(effectId), [onSelect, effectId]);

  return (
    <BlurCard label={label} icon={icon} isActive={isActive} isLoading={isLoading} isApplying={isApplying} onClick={handleClick} />
  );
});
